export type NotificationEventSeverity =
  "neutral" | "info" | "success" | "warning" | "error" | "critical" | "loading";

export type NotificationEventPersistence =
  "transient" | "persistent" | "acknowledgement";

export type NotificationType =
  | "vix-spike"
  | "system-update"
  | "connection-status"
  | "api-error"
  | "grok-auth"
  | "pre-market-reminder"
  | "news-alert"
  | "tilt-alert"
  | "trade-alert"
  | "iv-alert"
  | "riskflow-watch"
  | "risk-signal"
  | "agent-task"
  | "general";

export type ToastVariant =
  | "success"
  | "error"
  | "updating"
  | "info"
  | "reminder"
  | "vix"
  | "watch"
  | "risk-signal";

export type ToastPosition = "bottom-left" | "bottom-right";

/** RiskFlow is the only notification family allowed in the right-hand lane. */
export function notificationToastPosition(
  input: Pick<
    NotificationEventInput,
    "notificationType" | "legacyVariant" | "source" | "kind"
  >,
): ToastPosition {
  const isRiskFlow =
    input.notificationType === "riskflow-watch" ||
    input.notificationType === "risk-signal" ||
    input.legacyVariant === "risk-signal" ||
    /riskflow|risk[_ -]?signals?/i.test(`${input.source} ${input.kind}`);

  return isRiskFlow ? "bottom-right" : "bottom-left";
}

export interface NotificationEventAction {
  label: string;
  onClick: () => void;
}

export interface NotificationEvent {
  id: string;
  source: string;
  kind: string;
  severity: NotificationEventSeverity;
  title: string;
  body?: string;
  dedupeKey?: string;
  fingerprint: string;
  createdAt: number;
  updatedAt: number;
  expiresAt: number | null;
  persistence: NotificationEventPersistence;
  action?: NotificationEventAction;
  secondaryAction?: NotificationEventAction;
  nativeDeliveryEligible: boolean;
  readAt: number | null;
  dismissedAt: number | null;
  repeatCount: number;
  notificationType?: NotificationType;
  legacyVariant?: ToastVariant;
  legacyPosition?: ToastPosition;
  leadingGlyph?: string;
  riskScore?: number;
  persistentHistoryId?: string;
}

export interface NotificationEventInput {
  id?: string;
  source: string;
  kind: string;
  severity?: NotificationEventSeverity;
  title: string;
  body?: string;
  dedupeKey?: string;
  createdAt?: number;
  durationMs?: number | null;
  persistence?: NotificationEventPersistence;
  action?: NotificationEventAction;
  secondaryAction?: NotificationEventAction;
  nativeDeliveryEligible?: boolean;
  notificationType?: NotificationType;
  legacyVariant?: ToastVariant;
  legacyPosition?: ToastPosition;
  leadingGlyph?: string;
  riskScore?: number;
  persistentHistoryId?: string;
}
